/**
 * Helper function to check if a selection is an integration-level selection
 * @param {string} val - The selection value
 * @param {Object} loc - The location object from locationData
 * @returns {boolean} True if this is an integration-level selection
 */
export const isIntegrationSelection = (val, loc) => {
  // Check if this is an integration-level selection (e.g., "INTEGRATION::252")
  if (typeof val === 'string' && val.startsWith('INTEGRATION::')) {
    return true;
  }
  // Check if the location object is marked as an integration
  return loc?.isIntegration === true;
};

/**
 * Extracts integration IDs from selected integration-level values only (not individual locations)
 * @param {Array|string} locationValues - The selected location value(s)
 * @param {Array} locationData - The location data array containing location objects.
 *                                Each object should have:
 *                                - value: string - The location identifier used for matching
 *                                - integrationId: (string|number) - The integration ID to extract
 *                                - isIntegration: boolean - Whether this is an integration-level option
 * @returns {Array} Array of integration IDs (only from integration-level selections)
 */
export const extractIntegrationIds = (locationValues, locationData) => {
  // Validate locationData is defined and is an array
  if (!locationData || !Array.isArray(locationData)) {
    return [];
  }

  // Ensure locationValues is an array
  const selectedLocationValues = Array.isArray(locationValues)
    ? locationValues
    : [locationValues];

  // Create a Map for O(1) lookups instead of O(n) find operations
  const locationMap = new Map(locationData.map(l => [l.value, l]));

  // Extract integration IDs only from integration-level selections
  const integrationIds = selectedLocationValues
    .map(val => {
      const loc = locationMap.get(val);
      // Only return integrationId if this is an integration-level selection
      if (isIntegrationSelection(val, loc)) {
        return loc?.integrationId;
      }
      // Skip individual locations - they should only contribute to location_ids
      return null;
    })
    .filter(id => id !== undefined && id !== null);

  return integrationIds;
};

/**
 * Extracts location IDs from selected location values (excluding integration-level selections)
 * @param {Array|string} locationValues - The selected location value(s)
 * @param {Array} locationData - The location data array containing location objects.
 *                                Each object should have:
 *                                - value: string - The location identifier used for matching
 *                                - isIntegration: boolean - Whether this is an integration-level option
 *                                - locationId: number - The locationId from the API
 * @returns {Array} Array of location IDs (only from individual location selections, not integrations)
 */
export const extractLocationIds = (locationValues, locationData) => {
  // Validate locationData is defined and is an array
  if (!locationData || !Array.isArray(locationData)) {
    return [];
  }

  // Ensure locationValues is an array
  const selectedLocationValues = Array.isArray(locationValues)
    ? locationValues
    : [locationValues];

  // Create a Map for O(1) lookups instead of O(n) find operations
  const locationMap = new Map(locationData.map(l => [l.value, l]));

  // Extract location IDs from location data (only from individual locations, not integrations)
  const locationIds = selectedLocationValues
    .map(val => {
      const loc = locationMap.get(val);
      // Skip integration-level selections
      if (isIntegrationSelection(val, loc)) {
        return null;
      }
      // Return the locationId from the API (use nullish coalescing to handle 0 as valid)
      return loc?.locationId ?? null;
    })
    .filter(id => id !== undefined && id !== null);

  return locationIds;
};
